'use client';

import * as React from 'react';
import { GlobeIcon, PaletteIcon, CheckIcon } from 'lucide-react';
import { motion } from 'motion/react';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@workspace/ui/components/card';
import { Badge } from '@workspace/ui/components/badge';
import { cn } from '@workspace/ui/lib/utils';

const MotionCard = motion.create(Card);

const TEMPLATES = [
  { name: 'Elegant', color: 'bg-rose-100 border-rose-300' },
  { name: 'Modern', color: 'bg-slate-100 border-slate-300' },
  { name: 'Rustic', color: 'bg-amber-100 border-amber-300' },
];

export function BentoCustomersCard({
  className,
  ...other
}: React.ComponentPropsWithoutRef<typeof MotionCard>): React.JSX.Element {
  return (
    <MotionCard
      className={cn(
        'relative h-[300px] max-h-[300px] overflow-hidden',
        className
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      {...other}
    >
      <CardHeader>
        <CardTitle className="text-xl font-semibold">Wedding Website</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="line-clamp-2 text-sm text-muted-foreground">
          Create a stunning website in minutes with beautiful templates.
        </p>
        <div className="space-y-3 rounded-lg border p-4 shadow-xs">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <GlobeIcon className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">Your website</span>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Badge variant="secondary" className="text-xs">
                <CheckIcon className="mr-1 size-3" />
                Live
              </Badge>
            </motion.div>
          </div>
          
          <div className="flex gap-2">
            {TEMPLATES.map((template, index) => (
              <motion.div
                key={template.name}
                className={cn(
                  'flex-1 rounded-md border-2 p-2 text-center text-xs',
                  template.color,
                  index === 0 && 'ring-2 ring-primary ring-offset-1'
                )}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
              >
                {template.name}
              </motion.div>
            ))}
          </div>
          
          <motion.div
            className="flex items-center gap-2 text-sm text-muted-foreground"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
          >
            <PaletteIcon className="size-4" />
            <span>10+ customizable templates</span>
          </motion.div>
        </div>
      </CardContent>
    </MotionCard>
  );
}
